import React, { useEffect, useState } from 'react';
import { collection, getDocs, getDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloseIcon from "@mui/icons-material/Close";

const INCOMPLETE_USER_TOOLTIP =
  'Firebase Auth account exists, but users profile is missing. ' +
  'Edit this user or add the same email again to repair it.';

export default function UsersPage() {
  const { currentUser, isAdmin, refreshAdminStatus } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  // Delete confirmation dialog states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: 'user',
    password: ''
  });

  // Validation states
  const [formErrors, setFormErrors] = useState({});

  // Check if user is admin
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to access the Users management page.</Alert>
      </Box>
    );
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch users from Cloud Function (Firebase Auth + Firestore enrichment)
      const getUsersList = httpsCallable(functions, 'getUsersList');
      const result = await getUsersList();
      
      if (result.data && result.data.success) {
        setUsers(result.data.users);
      } else {
        throw new Error('Failed to fetch users from server');
      }
    } catch (err) {
      setError('Failed to load users.');
    }
    setLoading(false);
  };



  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!editingUser && !formData.password.trim()) {
      errors.password = 'Password is required for new users';
    } else if (formData.password.trim() && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }
    
    // Display name validation
    if (!formData.displayName.trim()) {
      errors.displayName = 'Display name is required';
    } else if (formData.displayName.trim().length < 2) {
      errors.displayName = 'Display name must be at least 2 characters long';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email || '',
        displayName: user.displayName || '',
        role: user.isAdmin ? 'admin' : 'user',
        password: ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        displayName: '',
        role: 'user',
        password: ''
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      email: '',
      displayName: '',
        role: 'user',
        password: ''
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setDialogLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update existing user via Cloud Function
        const updateUserByAdmin = httpsCallable(functions, 'updateUserByAdmin');
        const result = await updateUserByAdmin({
          userId: editingUser.id,
          email: formData.email.trim(),
          displayName: formData.displayName.trim(),
          password: formData.password || null, // Only update password if provided
          isAdmin: formData.role === 'admin',
        });
        
        // Check if the function call was successful
        if (result.data && result.data.success) {
          setSuccess('User updated successfully!');
        } else {
          throw new Error('User update failed');
        }
      } else {
        // Create new user via Cloud Function
        const createUserByAdmin = httpsCallable(functions, 'createUserByAdmin');
        const result = await createUserByAdmin({
          email: formData.email.trim(),
          password: formData.password,
          displayName: formData.displayName.trim(),
          isAdmin: formData.role === 'admin',
        });
        
        // Check if the function call was successful
        if (result.data && result.data.success) {
          setSuccess(
            result.data.repairedExistingUser
              ? 'Existing incomplete user repaired successfully.'
              : 'User created successfully.'
          );
        } else {
          throw new Error('User creation failed');
        }
      }
      handleCloseDialog();
      fetchUsers();
      
      // Refresh admin status in case the current user was just made admin
      await refreshAdminStatus();
    } catch (err) {
      // Handle specific error types from Cloud Functions
      let errorMessage = 'Failed to save user. Please try again.';
      
      if (err.code === 'functions/unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (err.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || 'Invalid input data. Please check your form.';
      } else if (err.code === 'functions/already-exists') {
        errorMessage = 'A user with this email address already exists.';
      } else if (err.code === 'functions/internal') {
        errorMessage = 'An internal error occurred. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.details) {
        errorMessage = err.details;
      }
      
      setError(errorMessage);
    }
    setDialogLoading(false);
  };


  const handleDeleteUser = async (userId, userEmail) => {
    const user = users.find(u => u.id === userId);
    
    // Set the user to delete and open confirmation dialog immediately
    setUserToDelete({ id: userId, email: userEmail, isPending: user?.pendingUser });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    const { id: userId, email: userEmail, isPending } = userToDelete;
    
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
    
    setDeletingUserId(userId);
    setError('');
    setSuccess('');

    try {
      // Fetch projects at deletion time to process them
      let projectsToProcess = [];
      
      if (!isPending) {
        const projectsRef = collection(db, 'projects');
        const projectsSnapshot = await getDocs(projectsRef);
        
        for (const projectDoc of projectsSnapshot.docs) {
          const projectId = projectDoc.id;
          const memberRef = doc(db, `projects/${projectId}/members/${userId}`);
          const memberSnap = await getDoc(memberRef);
          
          if (memberSnap.exists()) {
            const memberData = memberSnap.data();
            projectsToProcess.push({
              projectId,
              isOwner: memberData.role === 'owner'
            });
          }
        }
      }

      // Process each project
      for (const project of projectsToProcess) {
        if (project.isOwner) {
          // If user is owner, delete the entire project with all subcollections
          await deleteProjectWithSubcollections(project.projectId);
        } else {
          // If user is just a member, remove them from the project
          await removeUserFromProject(userId, project.projectId);
        }
      }

      // Call Cloud Function to delete user from Firebase Auth and Firestore
      const deleteUserByAdmin = httpsCallable(functions, 'deleteUserByAdmin');
      await deleteUserByAdmin({ userId });
      
      setSuccess(`User "${userEmail}" deleted successfully!`);
      fetchUsers();
    } catch (err) {
      // Handle specific error types from Cloud Functions
      let errorMessage = 'Failed to delete user. Please try again.';
      
      if (err.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to delete users.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || 'Invalid user ID.';
      } else if (err.code === 'functions/not-found') {
        errorMessage = 'User not found.';
      } else if (err.code === 'functions/internal') {
        errorMessage = 'An internal error occurred. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.details) {
        errorMessage = err.details;
      }
      
      setError(errorMessage);
    }
    
    setDeletingUserId(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  // Helper function to delete project with all subcollections
  const deleteProjectWithSubcollections = async (projectId) => {
    try {
      // Get all project members and clean up their userProjects collections
      const membersRef = collection(db, `projects/${projectId}/members`);
      const membersSnapshot = await getDocs(membersRef);
      
      // Remove project from all members' userProjects collections
      const cleanupPromises = membersSnapshot.docs.map(memberDoc => {
        const memberUserId = memberDoc.id;
        const userProjectRef = doc(db, `users/${memberUserId}/userProjects/${projectId}`);
        return deleteDoc(userProjectRef).catch(() => {
          // Continue with other cleanup even if one fails
        });
      });
      
      // Wait for all userProjects cleanup to complete
      await Promise.all(cleanupPromises);
      
      // Delete all subcollections recursively
      await deleteCollectionRecursively(`projects/${projectId}`);
      
      // Finally delete the project document itself
      await deleteDoc(doc(db, `projects/${projectId}`));
      
      
    } catch (error) {
      throw error;
    }
  };

  // Helper function to remove user from a project
  const removeUserFromProject = async (userId, projectId) => {
    try {
      // Remove user from project members
      const memberRef = doc(db, `projects/${projectId}/members/${userId}`);
      await deleteDoc(memberRef);
      
      // Remove project from user's userProjects collection
      const userProjectRef = doc(db, `users/${userId}/userProjects/${projectId}`);
      await deleteDoc(userProjectRef);
      
      
    } catch (error) {
      throw error;
    }
  };

  // Helper function to delete a collection and all its subcollections recursively
  const deleteCollectionRecursively = async (collectionPath) => {
    // Only call collection() if collectionPath has an odd number of segments
    if (collectionPath.split('/').length % 2 === 0) {
      // Even segments = document path, not a collection
      return;
    }
    const collectionRef = collection(db, collectionPath);
    const snapshot = await getDocs(collectionRef);
    
    const deletePromises = snapshot.docs.map(async (doc) => {
      // Recursively delete subcollections
      await deleteCollectionRecursively(`${collectionPath}/${doc.id}`);
      // Delete the document
      return deleteDoc(doc.ref);
    });
    
    await Promise.all(deletePromises);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      // Firestore timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      // Already a Date object
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      // String date
      date = new Date(timestamp);
    } else {
      // Try to create Date from whatever it is
      date = new Date(timestamp);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Users Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={dialogLoading}
        >
          Add User
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.email}
                      {user.id === currentUser?.uid && (
                        <Chip label="You" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.displayName || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={user.isAdmin ? <AdminPanelSettingsIcon /> : <PersonIcon />}
                      label={user.isAdmin ? 'Admin' : 'User'}
                      color={user.isAdmin ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.hasFirestoreProfile === false ? (
                      <Tooltip title={INCOMPLETE_USER_TOOLTIP}>
                        <Chip label="Incomplete" size="small" color="error" />
                      </Tooltip>
                    ) : user.pendingUser ? (
                      <Chip label="Pending" size="small" color="warning" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit user">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        disabled={user.id === currentUser?.uid || dialogLoading}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete user">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        disabled={user.id === currentUser?.uid || (deletingUserId !== null && deletingUserId !== user.id) || dialogLoading}
                        color="error"
                      >
                        {deletingUserId === user.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          {editingUser ? 'Edit User' : 'Add New User'}
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{
              padding: '0',
              my: 2,
            }}
          >
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={editingUser} // Can't change email for existing users
            />
            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              margin="normal"
              error={!!formErrors.displayName}
              helperText={formErrors.displayName}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="Role"
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="text"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              error={!!formErrors.password}
              helperText={formErrors.password || (editingUser ? 'Leave blank to keep current password, or enter new password (minimum 6 characters)' : 'Password for the new user (minimum 6 characters)')}
            />
            {!editingUser && (
              <Alert severity="info" sx={{ mt: 2 }}>
                The user will be created immediately and will receive an invitation email with their login credentials. They can then log in with their email and password.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: "16px 0 0" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={dialogLoading}
            >
              {dialogLoading ? <CircularProgress size={18} /> : (editingUser ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          Confirm User Deletion
          <IconButton
            onClick={handleCancelDelete}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '0', my: 2 }}>
          {userToDelete && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete user <strong>"{userToDelete.email}"</strong>?
              </Typography>
              
              {userToDelete.isPending ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This is a pending user. This action cannot be undone.
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  This will delete all projects where they are the owner and remove them from all project memberships. This action cannot be undone.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: "16px 0 0" }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            disabled={deletingUserId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deletingUserId !== null}
          >
            {deletingUserId !== null ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              'Delete User'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 
